// ==================================================================================
// Digital Approval System (DAS) - Pending Days Breakdown Modal
// ==================================================================================
// Drill-down popup for the "Total Pending Days" cell. Fetches the per-approver
// breakdown for one request and lists approver, stage, assigned/completed dates,
// and pending days. Read-only. Rendered via createPortal per the DAS modal
// conventions (fixed centered, scrollable body, fixed footer).
// ==================================================================================

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportsService } from 'api/services/das/reportsService';
import type { PendingDaysBreakdown } from 'api/types/das/reports.types';

interface PendingDaysBreakdownModalProps {
  isOpen: boolean;
  requestId: string | null;
  requestNumber?: string;
  onClose: () => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const statusColor = (status?: string | null): string => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'pre approved':
      return '#10b981';
    case 'rejected':
      return '#dc2626';
    case 'returned':
      return '#f59e0b';
    case 'pending':
      return '#2563eb';
    default:
      return '#6b7280';
  }
};

const PendingDaysBreakdownModal: React.FC<PendingDaysBreakdownModalProps> = ({
  isOpen,
  requestId,
  requestNumber,
  onClose,
}) => {
  const [data, setData] = useState<PendingDaysBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !requestId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setData(null);

    reportsService
      .getPendingBreakdown(requestId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load breakdown');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, requestId]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff',
          borderRadius: '0.5rem',
          width: 'min(680px, 94vw)',
          maxHeight: '84vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1001,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            flexShrink: 0,
            padding: '0.875rem 1.25rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>Pending Days Breakdown</h3>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>
              {requestNumber || data?.requestNumber || ''}
              {data ? `  ·  Total pending: ${data.totalPendingDays} day(s)` : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '1.4rem',
              lineHeight: 1,
              cursor: 'pointer',
              color: '#6b7280',
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '1rem 1.25rem' }}>
          {loading && <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading breakdown…</div>}
          {error && (
            <div
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderRadius: '0.375rem',
                padding: '0.625rem 0.875rem',
                fontSize: '0.85rem',
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#374151' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Approver</th>
                  <th style={thStyle}>Stage</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Assigned</th>
                  <th style={thStyle}>Completed</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Pending Days</th>
                </tr>
              </thead>
              <tbody>
                {data.approvers.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ ...tdStyle, color: '#6b7280', textAlign: 'center' }}>
                      No approvers found for this request.
                    </td>
                  </tr>
                )}
                {data.approvers.map((a, idx) => (
                  <tr key={`${a.sequenceOrder}-${idx}`}>
                    <td style={tdStyle}>{a.sequenceOrder > 0 ? a.sequenceOrder : '—'}</td>
                    <td style={tdStyle}>{a.approverName}</td>
                    <td style={tdStyle}>{a.stage || '—'}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: '0.72rem',
                          color: '#fff',
                          background: statusColor(a.approvalStatus),
                        }}
                      >
                        {a.approvalStatus || '—'}
                      </span>
                    </td>
                    <td style={tdStyle}>{formatDate(a.dateAssigned)}</td>
                    <td style={tdStyle}>{formatDate(a.dateCompleted)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{a.pendingDays}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            flexShrink: 0,
            padding: '0.75rem 1.25rem',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              padding: '0.4rem 1rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const thStyle: React.CSSProperties = {
  borderBottom: '2px solid #e5e7eb',
  padding: '0.5rem 0.5rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  borderBottom: '1px solid #f1f5f9',
  padding: '0.5rem 0.5rem',
  verticalAlign: 'top',
};

export default PendingDaysBreakdownModal;
